/**
 * TrickDisplay - Affichage des cartes posées sur le tapis
 *
 * Géométrie partagée avec PlayerHand via `useTableLayout` : même taille de
 * carte, recouvrement Nord/Sud en régime compact, main collée en bas.
 *
 * Z-index basé sur l'ordre de pose (pas la position du joueur).
 */

import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import Card from './Card';
import type { CardNumber, GamePlayer } from '../types/game';
import type { HandLayout } from '../types/app';
import { formatTablePlayerName } from '../utils/seatLabels';
import { useTableLayout } from '../hooks/useTableLayout';
import {
  seededRand,
  trickCardScatter,
  trickOrientation,
  type TrickScreenPos,
} from '../utils/tableLayout';
import '../styles/trick-display.css';

interface TrickDisplayProps {
  cards: CardNumber[];
  playerPositions: number[];
  playerCount: number;
  myPosition: number;
  players?: GamePlayer[];
  currentContract?: number;
  takerPosition?: number;
  partnerPosition?: number;
  partnerRevealed?: boolean;
  currentPlayer?: number;
  phase?: number;
  handCount?: number;
  handLayout?: HandLayout;
  animateCardPlay?: boolean;
  /** Orientation 90/180/45 selon le siège. Le tilt naturel est toujours actif. */
  trickCardRotation?: boolean;
  getLocalCardOrigin?: () => { rect: DOMRect; rotation: number } | null;
  isLive?: boolean;
}

const ContractNames: Record<number, string> = {
  0: 'Passe',
  1: 'Petite',
  2: 'Garde',
  3: 'Garde Sans',
  4: 'Garde Contre',
};

function getOpponentFlyVars(
  screenPos: string,
  card: CardNumber,
  endRot: number,
): Record<string, string> {
  const randX = (seededRand(card) - 0.5) * 40;
  const randY = (seededRand(card + 100) - 0.5) * 40;
  const randR = (seededRand(card + 200) - 0.5) * 14;
  const end = { '--fly-end-r': `${endRot}deg` };

  switch (screenPos) {
    case 'north':
      return { '--fly-x': `${randX}px`, '--fly-y': '-55vh', '--fly-r': `${randR}deg`, ...end };
    case 'east':
      return { '--fly-x': '55vw', '--fly-y': `${randY}px`, '--fly-r': `${randR}deg`, ...end };
    case 'west':
      return { '--fly-x': '-55vw', '--fly-y': `${randY}px`, '--fly-r': `${randR}deg`, ...end };
    case 'north-east':
      return { '--fly-x': `${20 + randX}px`, '--fly-y': '-55vh', '--fly-r': `${randR}deg`, ...end };
    case 'north-west':
      return { '--fly-x': `${-20 + randX}px`, '--fly-y': '-55vh', '--fly-r': `${randR}deg`, ...end };
    default:
      return { '--fly-x': '0px', '--fly-y': '250px', '--fly-r': '0deg', ...end };
  }
}

function toScreenPos(playerPos: number, myPosition: number, playerCount: number): TrickScreenPos {
  const relativePos = (playerPos - myPosition + playerCount) % playerCount;
  const positionMap: Record<number, Record<number, TrickScreenPos>> = {
    3: { 0: 'south', 1: 'east', 2: 'west' },
    4: { 0: 'south', 1: 'east', 2: 'north', 3: 'west' },
    5: { 0: 'south', 1: 'east', 2: 'north-east', 3: 'north-west', 4: 'west' },
  };
  return positionMap[playerCount]?.[relativePos] || 'center';
}

export default function TrickDisplay({
  cards,
  playerPositions,
  playerCount,
  myPosition,
  players = [],
  currentContract = 0,
  takerPosition,
  partnerPosition,
  partnerRevealed = false,
  currentPlayer,
  phase = 6,
  handCount = 0,
  handLayout = 'flat',
  animateCardPlay = false,
  trickCardRotation = false,
  getLocalCardOrigin,
  isLive = false,
}: TrickDisplayProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { layout } = useTableLayout({ handCount, handLayout });

  const cardsData = useMemo(() => {
    return cards.map((card, playOrder) => {
      const playerPos = playerPositions[playOrder];
      const screenPos = toScreenPos(playerPos, myPosition, playerCount);
      return {
        card,
        playOrder,
        playerPos,
        screenPos,
        player: players[playerPos],
        zIndex: playOrder + 1,
      };
    });
  }, [cards, playerPositions, myPosition, playerCount, players]);

  const prevSeenRef = useRef(new Set<CardNumber>());

  const newCardSet = useMemo(() => {
    if (!animateCardPlay) return new Set<CardNumber>();
    return new Set(cards.filter(c => !prevSeenRef.current.has(c)));
  }, [cards, animateCardPlay]);

  useLayoutEffect(() => {
    prevSeenRef.current = new Set(cards);
  }, [cards]);

  useLayoutEffect(() => {
    if (!animateCardPlay || newCardSet.size !== 1) return;

    const [card] = newCardSet;
    const idx = cards.indexOf(card);
    const data = cardsData[idx];
    if (!data || data.screenPos !== 'south') return;

    const origin = getLocalCardOrigin?.();
    const el = containerRef.current?.querySelector(
      `[data-card-number="${card}"]`,
    ) as HTMLElement | null;
    if (!el) return;

    const endRot =
      (trickCardRotation ? trickOrientation('south') : 0) + trickCardScatter(card).rot;

    if (origin) {
      const lastRect = el.getBoundingClientRect();
      const dx = (origin.rect.left + origin.rect.width / 2) - (lastRect.left + lastRect.width / 2);
      const dy = (origin.rect.top + origin.rect.height / 2) - (lastRect.top + lastRect.height / 2);

      el.style.translate = `${dx}px ${dy}px`;
      el.style.rotate = `${origin.rotation}deg`;
      void el.offsetWidth;

      el.style.transition =
        'translate 0.5s cubic-bezier(0.16, 1, 0.3, 1), ' +
        'rotate 0.5s cubic-bezier(0.16, 1, 0.3, 1)';
      el.style.translate = '0 0';
      el.style.rotate = `${endRot}deg`;

      const tid = setTimeout(() => {
        el.style.translate = '';
        el.style.transition = '';
        // On laisse `rotate` à endRot : c'est la même valeur que le style React.
        // La vider ici faisait un second bascule (transform + rotate se composaient).
      }, 550);
      return () => clearTimeout(tid);
    }

    el.classList.remove('trick-card-flip');
    el.classList.add('trick-card-fly-in');
    el.style.setProperty('--fly-x', '0px');
    el.style.setProperty('--fly-y', '250px');
    el.style.setProperty('--fly-r', '0deg');
    el.style.setProperty('--fly-end-r', `${endRot}deg`);
  }, [cards, cardsData, animateCardPlay, newCardSet, getLocalCardOrigin, trickCardRotation]);

  const getCardStyle = (data: typeof cardsData[0]): React.CSSProperties => {
    const { screenPos, zIndex, card } = data;
    const {
      cardWidth,
      northZoneY,
      southZoneY,
      middleZoneY,
      containerWidth,
      sideMargin,
      trickLateralShift,
    } = layout;

    const centerX = containerWidth / 2;
    const scatter = trickCardScatter(card);
    const orient = trickCardRotation ? trickOrientation(screenPos) : 0;

    let top = middleZoneY;
    let left = centerX - cardWidth / 2;

    switch (screenPos) {
      case 'north':
        top = northZoneY;
        left = centerX - cardWidth / 2 - trickLateralShift;
        break;
      case 'south':
        top = southZoneY;
        left = centerX - cardWidth / 2 + trickLateralShift;
        break;
      case 'east':
        top = middleZoneY;
        left = containerWidth - cardWidth - sideMargin;
        break;
      case 'west':
        top = middleZoneY;
        left = sideMargin;
        break;
      case 'north-east':
        top = northZoneY;
        left = centerX + cardWidth * 0.2;
        break;
      case 'north-west':
        top = northZoneY;
        left = centerX - cardWidth * 1.2;
        break;
      default:
        break;
    }

    return {
      position: 'absolute',
      top: `${top + scatter.dy}px`,
      left: `${left + scatter.dx}px`,
      zIndex,
      rotate: `${orient + scatter.rot}deg`,
      transformOrigin: 'center center',
    };
  };

  const PlayerLabel = ({ playerPos }: { playerPos: number }) => {
    const player = players[playerPos];
    if (!player) return null;

    const isTaker = takerPosition === playerPos;
    const isRevealedPartner = partnerRevealed && partnerPosition === playerPos;
    const isSelfCalled =
      isRevealedPartner && partnerPosition === takerPosition && playerPos === takerPosition;
    const isCurrentPlayer = currentPlayer === playerPos;
    const mustBid = isCurrentPlayer && phase === 2;
    const hasPlayedInTrick = playerPositions.includes(playerPos);
    const mustPlay = isCurrentPlayer && phase === 6 && !hasPlayedInTrick;
    const playerBid = player.bid;
    const hasPassed = playerBid === 0 && phase === 2;

    return (
      <div className={`trick-player-label ${isTaker ? 'is-taker' : ''} ${(mustPlay || mustBid) ? 'must-play' : ''}`}>
        <div className="trick-player-header">
          <span className="trick-player-name">
            {formatTablePlayerName(
              player.pseudo || `Joueur ${playerPos + 1}`,
              playerPos,
              myPosition,
              playerCount,
              isLive,
            )}
          </span>
          {(mustPlay || mustBid) && <span className="trick-turn-indicator">▶</span>}
        </div>
        {phase === 2 && hasPassed && (
          <span className="trick-player-bid passed">Passe</span>
        )}
        {phase === 2 && playerBid !== undefined && playerBid > 0 && (
          <span className="trick-player-bid active">{ContractNames[playerBid]}</span>
        )}
        {phase !== 2 && isTaker && currentContract > 0 && (
          <span className="trick-player-contract">{ContractNames[currentContract]}</span>
        )}
        {isRevealedPartner && (
          <span className={`trick-player-partner ${isSelfCalled ? 'self-called' : ''}`}>
            {isSelfCalled ? 'auto-appelé' : 'Appelé'}
          </span>
        )}
        {player.tricksWon !== undefined && player.tricksWon > 0 && (
          <span className="trick-player-tricks">{player.tricksWon} pli{player.tricksWon > 1 ? 's' : ''}</span>
        )}
      </div>
    );
  };

  const getPlayerPositions = () => {
    const positions: Record<string, number> = {};
    const relToAbs = (rel: number) => (myPosition + rel) % playerCount;

    if (playerCount === 3) {
      positions.south = myPosition;
      positions.east = relToAbs(1);
      positions.west = relToAbs(2);
    } else if (playerCount === 4) {
      positions.south = myPosition;
      positions.east = relToAbs(1);
      positions.north = relToAbs(2);
      positions.west = relToAbs(3);
    } else if (playerCount === 5) {
      positions.south = myPosition;
      positions.east = relToAbs(1);
      positions.northEast = relToAbs(2);
      positions.northWest = relToAbs(3);
      positions.west = relToAbs(4);
    }

    return positions;
  };

  const playerPositionsMap = getPlayerPositions();
  const showLabels = phase === 2 || phase === 6;
  const showSouthLabel = showLabels && !layout.compact;

  useEffect(() => {
    const gameTable = document.querySelector('.game-table');
    if (!gameTable) return;
    const el = gameTable as HTMLElement;
    el.style.setProperty('--hand-top', `${layout.handTop}px`);
    el.style.setProperty('--hand-band-height', `${layout.handBandHeight}px`);
    el.style.setProperty('--table-card-width', `${layout.cardWidth}px`);
    el.style.setProperty('--table-card-height', `${layout.cardHeight}px`);
    el.style.setProperty('--hand-padding-x', `${layout.handPaddingX}px`);
  }, [layout.handTop, layout.handBandHeight, layout.cardWidth, layout.cardHeight, layout.handPaddingX]);

  const {
    containerWidth,
    cardWidth,
    cardHeight,
    northZoneY,
    southZoneY,
    middleZoneY,
    sideMargin,
    labelBand,
  } = layout;
  const centerX = containerWidth / 2;

  return (
    <div
      ref={containerRef}
      className={`trick-display trick-display-${playerCount}p${layout.compact ? ' trick-display-compact' : ''}${layout.labelStyle === 'chip' ? ' trick-display-chips' : ''}`}
      style={{ height: `${layout.handTop}px` }}
    >
      {showLabels && playerPositionsMap.north !== undefined && (
        <div
          className="trick-label trick-label-north"
          style={{
            top: `${Math.max(2, northZoneY - labelBand)}px`,
            left: `${centerX}px`,
            transform: 'translateX(-50%)',
          }}
        >
          <PlayerLabel playerPos={playerPositionsMap.north} />
        </div>
      )}

      {showLabels && playerPositionsMap.northEast !== undefined && (
        <div
          className="trick-label trick-label-north-east"
          style={{
            top: `${Math.max(2, northZoneY - labelBand)}px`,
            left: `${centerX + cardWidth * 0.7}px`,
            transform: 'translateX(-50%)',
          }}
        >
          <PlayerLabel playerPos={playerPositionsMap.northEast} />
        </div>
      )}
      {showLabels && playerPositionsMap.northWest !== undefined && (
        <div
          className="trick-label trick-label-north-west"
          style={{
            top: `${Math.max(2, northZoneY - labelBand)}px`,
            left: `${centerX - cardWidth * 0.7}px`,
            transform: 'translateX(-50%)',
          }}
        >
          <PlayerLabel playerPos={playerPositionsMap.northWest} />
        </div>
      )}

      {showLabels && playerPositionsMap.east !== undefined && (
        <div
          className="trick-label trick-label-east"
          style={{
            top: `${middleZoneY + cardHeight / 2}px`,
            right: `${Math.max(4, sideMargin - cardWidth - 8)}px`,
            transform: 'translateY(-50%)',
          }}
        >
          <PlayerLabel playerPos={playerPositionsMap.east} />
        </div>
      )}

      {showLabels && playerPositionsMap.west !== undefined && (
        <div
          className="trick-label trick-label-west"
          style={{
            top: `${middleZoneY + cardHeight / 2}px`,
            left: `${Math.max(4, sideMargin - cardWidth - 8)}px`,
            transform: 'translateY(-50%)',
          }}
        >
          <PlayerLabel playerPos={playerPositionsMap.west} />
        </div>
      )}

      {cardsData.map(data => {
        const isNew = newCardSet.has(data.card);
        const isSouth = data.screenPos === 'south';
        const useFlyIn = animateCardPlay && isNew && !isSouth;
        const useFlip = animateCardPlay && isNew && isSouth;

        const endRot =
          (trickCardRotation ? trickOrientation(data.screenPos) : 0) + trickCardScatter(data.card).rot;
        const flyVars = useFlyIn ? getOpponentFlyVars(data.screenPos, data.card, endRot) : {};
        const className = [
          'trick-card',
          `trick-card-${data.screenPos}`,
          useFlyIn && 'trick-card-fly-in',
          useFlip && 'trick-card-flip',
          animateCardPlay && 'trick-card-no-default',
        ].filter(Boolean).join(' ');

        return (
          <div
            key={`trick-card-${data.card}-${data.playOrder}`}
            data-card-number={data.card}
            className={className}
            style={{ ...getCardStyle(data), ...flyVars } as React.CSSProperties}
          >
            <Card
              card={data.card}
              width={layout.cardWidth}
              height={layout.cardHeight}
            />
          </div>
        );
      })}

      {showSouthLabel && playerPositionsMap.south !== undefined && (
        <div
          className="trick-label trick-label-south"
          style={{
            top: `${southZoneY + cardHeight + 8}px`,
            left: `${centerX}px`,
            transform: 'translateX(-50%)',
            zIndex: 40,
          }}
        >
          <PlayerLabel playerPos={playerPositionsMap.south} />
        </div>
      )}
    </div>
  );
}
